import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isEqual, get } from 'lodash';
import { fetchCompareData } from '@store/comparison/actions';
import { addCompareSharingTab } from '@store/comparison/sharing/actions';
import { getConfig as getCompareConfig } from '@store/comparison/config/actions';
import { selectors as tabsStoreSelector } from '@store/dashboards/tabs';
import { compareSharingResponseSelector } from '@store/comparison/sharing/selectors';
import { parseMetricsFn } from '@store/common/persistence';
import { supportedMetricsSelector } from '@store/common/selectors';
import getGranularity from '@utils/granularity';
import { getAllowedGranularity } from '@components/common/GranularitySelect/utils';
import Migrate from './Migrate';

function parseConfig(tabData) {
  // while saving, compare renders with null tab data
  if (tabData === null) {
    return tabData;
  }
  const updatedTabData = {
    ...tabData,
  };
  try {
    const { isLiveMode = false, interval } = tabData.config;
    const { granularity } = interval;
    // validate granularity
    if (granularity && !isLiveMode) {
      const allowedValues = getAllowedGranularity(interval);
      // selected granularity is invalid, update config
      if (!allowedValues.includes(granularity)) {
        interval.granularity = getGranularity({
          from: interval.from,
          to: interval.to,
          minuteGranularity: true,
        });
      }
    }
    updatedTabData.config.interval = interval;
  } catch (err) {
    // ignore
  }

  return updatedTabData;
}

const CompareWrapper = ({
  id,
  tabs,
  fetchCompareData: fetchGraphData,
  getConfig,
  isSharing,
  addCompareSharingTabAction,
  supportedMetrics,
}) => {
  const parseMetrics = useMemo(() => parseMetricsFn(supportedMetrics), [supportedMetrics]);
  const [payload, setPayload] = useState({});

  useEffect(() => {
    if (isSharing) {
      addCompareSharingTabAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharing]);

  // validate and parse config
  const tabData = useMemo(() => {
    const data = get(tabs, `tabsData.${id}`, null);
    return parseConfig(data);
  }, [tabs, id]);

  useEffect(() => {
    const tData = tabData || get(tabs, `tabsData.${id}`, null);
    if (tData) {
      const metrics = parseMetrics(tData.config.metricList);
      const tDataConfig = {
        ...tData.config,
        zoomInterval: null,
        metricList: [...metrics].sort(),
        filterIndices: null,
      };
      const payloadConfig = {
        ...payload,
        zoomInterval: null,
      };
      if (!isEqual(tDataConfig, payloadConfig)) {
        const { config } = tData;
        const updatedConfig = {
          ...config,
          metricList: metrics,
        };
        fetchGraphData(id, updatedConfig);
      }
      setPayload(tDataConfig);
    } else if (!isSharing) {
      getConfig(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData, id, isSharing]);

  return <Migrate isSharing={isSharing} tabData={tabData} id={id} />;
};

const selector = createSelector(
  tabsStoreSelector,
  supportedMetricsSelector,
  compareSharingResponseSelector,
  (tabsStore, supportedMetrics) => ({
    tabs: tabsStore,
    supportedMetrics,
  }),
);

export default connect(selector, {
  fetchCompareData,
  getConfig: getCompareConfig,
  addCompareSharingTabAction: addCompareSharingTab,
})(CompareWrapper);
